import { useMemo, useState } from "react";
import {
  Datagrid,
  FunctionField,
  List,
  ReferenceInput,
  SelectInput,
  SimpleList,
  TextField,
  TopToolbar,
  CreateButton,
  useCreatePath,
} from "react-admin";
import type { RaRecord } from "react-admin";
import { Box, Button, Tab, Tabs, useMediaQuery } from "@mui/material";
import { Link } from "react-router-dom";
import { formatListDate, formatMajlisStartTimeLabel } from "@/utils/date-format";

function ListActions() {
  return (
    <TopToolbar>
      <CreateButton />
    </TopToolbar>
  );
}

const venueFilter = (
  <ReferenceInput key="venueId" source="venueId" reference="miqaatVenues" alwaysOn perPage={100}>
    <SelectInput optionText="name" label="Venue" resettable />
  </ReferenceInput>
);

export default function MiqaatList() {
  const [period, setPeriod] = useState<"all" | "upcoming" | "past">("all");
  const isNarrow = useMediaQuery((theme: { breakpoints: { down: (key: string) => string } }) =>
    theme.breakpoints.down("md")
  );
  const createPath = useCreatePath();
  const attendanceBase = createPath({ resource: "miqaatAttendance", type: "create" });

  const attendancePathForRecord = (record: RaRecord) =>
    `${attendanceBase}?miqaatId=${encodeURIComponent(String(record.id))}`;

  const showPathForRecord = (record: RaRecord) =>
    createPath({ resource: "miqaats", type: "show", id: record.id });

  const filter = useMemo(() => (period === "all" ? {} : { period }), [period]);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, bgcolor: "background.paper" }}>
        <Tabs value={period} onChange={(_, v) => setPeriod(v)} aria-label="Miqaat by period">
          <Tab label="All" value="all" />
          <Tab label="Upcoming" value="upcoming" />
          <Tab label="Past" value="past" />
        </Tabs>
      </Box>
      <List resource="miqaats" filter={filter} filters={[venueFilter]} actions={<ListActions />}>
        {isNarrow ? (
          <SimpleList
            primaryText={(r) => String(r.name ?? "—")}
            secondaryText={(r) =>
              `${r.dateMode === "PERMANENT" ? `Hijri ${String(r.hijriDay ?? "—")} · month ${String(r.hijriMonth ?? "—")}` : r.englishDate ? formatListDate(String(r.englishDate)) : "—"} · ${String(r.mealType ?? "—")} · ${formatMajlisStartTimeLabel(typeof r.startTime === "string" ? r.startTime : undefined)}`
            }
            tertiaryText={(r) => String(r.venue ?? "—")}
            rightIcon={(r) => (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Button
                  size="small"
                  variant="outlined"
                  component={Link}
                  to={attendancePathForRecord(r)}
                >
                  Attendance
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  component={Link}
                  to={showPathForRecord(r)}
                  onClick={(e) => e.stopPropagation()}
                >
                  Details
                </Button>
              </Box>
            )}
          />
        ) : (
          <Datagrid bulkActionButtons={false} rowClick={false}>
            <TextField source="name" />
            <FunctionField
              label="Date"
              render={(record) =>
                record.dateMode === "PERMANENT"
                  ? `Hijri ${String(record.hijriDay ?? "—")} (${String(record.hijriMonth ?? "—")})`
                  : record.englishDate
                    ? formatListDate(String(record.englishDate))
                    : "—"
              }
            />
            <TextField source="mealType" />
            <FunctionField
              label="Start time"
              render={(record) =>
                formatMajlisStartTimeLabel(
                  typeof record.startTime === "string" ? record.startTime : undefined
                )
              }
            />
            <TextField source="venue" />
            <FunctionField
              label="Attendance"
              render={(record) => (
                <Button
                  size="small"
                  variant="outlined"
                  component={Link}
                  to={attendancePathForRecord(record)}
                >
                  Mark Attendance
                </Button>
              )}
            />
            <FunctionField
              label="Details"
              render={(record) => (
                <Button
                  size="small"
                  variant="outlined"
                  component={Link}
                  to={showPathForRecord(record)}
                  onClick={(e) => e.stopPropagation()}
                >
                  View
                </Button>
              )}
            />
          </Datagrid>
        )}
      </List>
    </>
  );
}
